import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const HowToUseADUAsHomeOffice = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-home-office" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-background to-muted py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            How to Use Your ADU as a Home Office
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            Build your perfect WFH retreat. With an ADU office just steps from your home, you'll enjoy privacy, productivity, and a professional environment without the commute.
          </p>
          
          {/* Top CTA Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* ADU Office Photos Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/3ee7f03f-a3bc-4253-87c4-f2ad9a7a4664.png" 
                  alt="Interior with desk and natural lighting"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/07742ba5-337c-4155-8432-cece1acdbe88.png" 
                  alt="Exterior garden view with glass doors"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/c0755f22-fb00-4b51-a352-cb295a8dd890.png" 
                  alt="Multi-functional guest-office combo"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why an ADU Office Beats Working Inside */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[hsl(var(--brand-primary))]">
            Why an ADU Office Beats Working Inside
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Perfect Work-Life Separation</h3>
                <p className="text-muted-foreground">
                  Step out of your home and into your professional space. Create clear boundaries between work and personal life with a dedicated office environment.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Increased Focus & Productivity</h3>
                <p className="text-muted-foreground">
                  Eliminate household distractions and interruptions. Your ADU office provides the quiet, professional environment you need to maximize productivity.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Zero Commute Time</h3>
                <p className="text-muted-foreground">
                  Save hours daily with your office just steps away. No traffic, no parking, no stress - just a short walk to your productive workspace.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Professional Meeting Space</h3>
                <p className="text-muted-foreground">
                  Host client meetings and video calls in a dedicated, professional environment without worrying about household noise or interruptions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Design Tips Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[hsl(var(--brand-primary))]">
            Design Tips for the Perfect WFH Studio
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Maximize Natural Light</h3>
                <p className="text-muted-foreground">
                  Install skylights and large windows to flood your office with natural light. Good lighting reduces eye strain and boosts mood and energy throughout the workday.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Soundproofing Solutions</h3>
                <p className="text-muted-foreground">
                  Incorporate sound-dampening materials and proper insulation to ensure quiet, focused work sessions and professional video calls.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Smart Storage Systems</h3>
                <p className="text-muted-foreground">
                  Built-in shelving, filing systems, and hidden storage keep your office organized and clutter-free while maximizing the usable space.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Dual-Use Guest Features</h3>
                <p className="text-muted-foreground">
                  Design flexible spaces that can serve as both office and guest room with murphy beds, convertible furniture, and multipurpose layouts.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Smart Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[hsl(var(--brand-primary))]">
            Smart Features That Make a Difference
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">High-Speed Connectivity</h3>
                <p className="text-muted-foreground">
                  Dedicated ethernet ports, fiber-ready infrastructure, and strong Wi-Fi coverage ensure seamless video calls and fast file transfers.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Energy Efficiency</h3>
                <p className="text-muted-foreground">
                  LED lighting, smart thermostats, and energy-efficient windows keep operating costs low while maintaining a comfortable work environment.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Ergonomic Integration</h3>
                <p className="text-muted-foreground">
                  Built-in desk solutions, proper outlet placement, and cable management systems create an ergonomic workspace that reduces strain and increases comfort.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Climate Control</h3>
                <p className="text-muted-foreground">
                  Independent HVAC systems ensure your office stays comfortable year-round without affecting your main home's energy usage.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Call-to-Action Section */}
      <section className="py-16 px-4 bg-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary-foreground))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Build Your Perfect Home Office?</h2>
          <p className="text-lg mb-12 opacity-90">
            Let's design an ADU office that transforms your work-from-home experience.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 w-full"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/our-work" 
                className="w-full"
              >
                🏡 Our Portfolio ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/faq" 
                className="w-full"
              >
                ❓ Our FAQ ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
      <FloatingADUWizard />
    </div>
  );
};

export default HowToUseADUAsHomeOffice;